import React, { useEffect, useRef, useState } from "react"
import { Checkbox, Input, Modal } from "antd"

/** An option a net can hold: its label (what the net stores) and the name it is shown under. */
export interface NetOption {
  value: string
  label: string
  /** The label itself, as the tooltip, when the name shown differs. */
  title?: string
}

export interface AddNetModalProps {
  open: boolean
  title: string
  /** The options the net can be made of, in order. */
  options: NetOption[]
  onCancel: () => void
  /** Adds the net; returns why it was refused, or null once it is added. */
  onConfirm: (name: string, members: string[]) => string | null
}

/**
 * Asks for a net's name and members. Each opening starts empty. A refusal
 * from `onConfirm` is shown under the name and the dialog stays open; the
 * caller closes it once the net is added. The members are given by their
 * labels (`value`), whatever name they are shown under.
 */
const AddNetModal: React.FC<AddNetModalProps> = ({
  open,
  title,
  options,
  onCancel,
  onConfirm,
}) => {
  const [name, setName] = useState("")
  const [members, setMembers] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const anchorRef = useRef<HTMLSpanElement>(null)
  // The dialog renders inside the spec, so it gets the --qc-* tokens.
  const container = (): HTMLElement =>
    anchorRef.current?.closest<HTMLElement>(".tabspec-root") ?? document.body

  useEffect(() => {
    if (!open) return
    setName("")
    setMembers([])
    setError(null)
  }, [open])

  const confirm = (): void => setError(onConfirm(name, members))

  return (
    <>
      <span ref={anchorRef} hidden />
      <Modal
        open={open}
        title={title}
        okText="Add"
        onOk={confirm}
        onCancel={onCancel}
        getContainer={container}
        destroyOnHidden
      >
        <div className="ts-net-form">
          <Input
            aria-label="Net name"
            placeholder="Name"
            value={name}
            status={error ? "error" : undefined}
            onChange={(e) => {
              setName(e.target.value)
              setError(null)
            }}
            onPressEnter={confirm}
          />
          {error && (
            <p className="ts-error" role="alert">
              {error}
            </p>
          )}
          <Checkbox.Group
            className="ts-net-form__members"
            aria-label="Members"
            options={options}
            value={members}
            onChange={(chosen) => {
              setMembers(chosen as string[])
              setError(null)
            }}
          />
        </div>
      </Modal>
    </>
  )
}

export default AddNetModal
